import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import { operatorName } from '../../../../config/operator';
import { TDeliveryVenue } from '../../../../contexts/delivery-venue/delivery-venue-context';
import { filterDeliveryVenues } from '../../../../services/api/delivery-venue';
import DeliveryVenueCard from './DeliveryVenueCard';
import icon_search from '../../../../assets/images/search.svg';
import { useSearchParams } from "react-router-dom";
import useOperatorContext from "../../../../contexts/operator/useOperatorContext";

interface DeliveryVenueFilterProps {
    deliveryVenues: TDeliveryVenue[];
}

const DeliveryVenueFilter = ({ deliveryVenues = [] }: DeliveryVenueFilterProps) => {
    const operatorContext = useOperatorContext();

    const [filteredDeliveryVenues, setFilteredDeliveryVenues] = useState<TDeliveryVenue[]>(deliveryVenues ?? []);
    const [mappedDeliveryVenues, setMappedDeliveryVenues] = useState<TDeliveryVenue[]>(deliveryVenues ?? []);
    const [filter, setFilter] = useState('');
    const [locationLoading, setLocationLoading] = useState(false);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        let filtered: TDeliveryVenue[] = [];

        if (filter.length > 2) {
            filterDeliveryVenues(operatorName, filter).then((data: { uid: string }[]) => {
                filtered = deliveryVenues.filter((deliveryVenue) => {
                    // Search by name
                    return data.findIndex(dv => dv.uid === deliveryVenue.uid) !== -1;
                });

                setFilteredDeliveryVenues(filtered);
            });
        } else {
            filtered = deliveryVenues;

            setFilteredDeliveryVenues(filtered);
        }
    }, [filter]);

    return (
        <StyledForm nearest={(operatorContext.operator?.theme.v4.location_search ?? false).toString()}>
            { operatorContext.operator?.theme.v4.location_search ? (
                <InputWrapper input={filter} controlId={"formSearch"}>
                    <div className={'filter-input-wrapper'}>
                        <Form.Control
                            size="lg"
                            type="text"
                            className=""
                            placeholder={`${ filteredDeliveryVenues.length > 0 ? `Search ${filteredDeliveryVenues.length} location${filteredDeliveryVenues.length > 1 ? 's' : ''} by city, street or postcode` : 'Search by city, street or postcode'}`}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value.trim())}
                        />
                        <ClearFormBtn
                            className={`${ filter.length > 0 ? 'with-input' : 'no-input' }`}
                            onClick={() => setFilter('')}>
                            <span className="material-symbols-outlined">close</span>
                        </ClearFormBtn>
                        <span className="material-symbols-outlined filter-input__search">search</span>
                    </div>
                </InputWrapper>
            ) : null }

            <div className="wrap-delivery-venues">
                {
                    filteredDeliveryVenues.map((deliveryVenue) => (
                        <DeliveryVenueCard
                            deliveryVenue={deliveryVenue}
                            key={`venues_${deliveryVenue.uid}`}
                        />
                    ))
                }
            </div>
        </StyledForm>
    );
};

const StyledForm = styled(Form.Group)<{ nearest: string }>`
    .invalid-alert {
        margin: 0 8px;
    }

    .wrap-delivery-venues {
        ${(props) => (props.nearest === 'true' ? `margin-top: 20px;` : `margin-top: 12px;`)}
        padding: 0 60px;
        @media screen and (max-width: 767px) {
            padding: 0;
        }
    }

    .sites-count {
        margin-top: 20px;
        padding: 0 48px;
        color: var(--text-grey);
        line-height: 24px;
        font-size: var(--core_text_ui_lg);

        @media screen and (min-width: 576px) {
            font-size: var(--core_text_ui_lg_desktop);
        }

        @media screen and (max-width: 767px) {
            padding: 12px 20px 0 20px;
        }
    }
`;

const ClearFormBtn = styled.button`
    border: 0;
    background-color: transparent;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);

    color: var(--text-grey);
    font-size: 16px;
    line-height: 24px;

    align-items: center;

    img { margin-left: 8px; }

    &.with-input {display: flex; }
    &.no-input { display: none; }
`;

const InputWrapper = styled(Form.Group)<{ input: string }>`
    position: relative;
    padding: 0 60px;

    @media screen and (max-width: 767px) {
        padding: 0 20px;
    }

    .form-control {
        padding: 18px 16px 18px 48px;
        text-overflow: ellipsis;
        ${(props) => (props.input.length > 0 ? `padding-right: 48px;` : null)}
    }

    .filter-input-wrapper {
        position: relative;
    }

    .filter-input__search {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        padding: 4px;
        border-radius: var(--radius_m);
        color: var(--text-black);
        font-size: 24px;
        line-height: 24px;
    }

    .form-label {
        font-size: var(--core_text_ui_lg);
        @media screen and (min-width: 576px) {
            font-size: var(--core_text_ui_lg_desktop);
        }
        line-height: 24px;
        color: var(--text-black);
        margin-bottom: 12px;
    }

    .location_icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 14px;
        cursor: pointer;
    }

    .spinner-grow {
        height: 18px;
        width: 18px;
    }
`;

const LocationCount = styled.div`
    padding: 0 48px;
    font-size: 16px;
    line-height: 24px;
    color: var(--text-black);
    margin-bottom: 8px;

    @media screen and (max-width: 767px) {
        padding: 0 20px;
    }
`;

export default DeliveryVenueFilter;
